import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface ResolutionFieldConfig {
  id: string;
  label: string;
  placeholder: string;
  assistText: string;
  suffix?: string;
  options?: string[];
  defaultValue?: string;
  /** If true, renders as a 1-click attestation button (no typing) */
  attestation?: boolean;
  /** Pre-written text shown on the attestation button */
  attestationLabel?: string;
  /** Label shown after attestation is confirmed */
  attestationConfirmedLabel?: string;
}

interface InlineResolutionFieldProps {
  config: ResolutionFieldConfig;
  value: string;
  onChange: (id: string, value: string) => void;
  /** Called when user resolves with "save to template" enabled */
  onSaveToTemplate?: (id: string, value: string) => void;
  /** When true, renders a DocuSign-style yellow highlight/arrow to draw attention */
  highlighted?: boolean;
}

export function InlineResolutionField({ config, value, onChange, onSaveToTemplate, highlighted }: InlineResolutionFieldProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isTypingOther, setIsTypingOther] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [saveToTemplate, setSaveToTemplate] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isResolved = value.trim().length > 0;

  // Build dropdown options: always include defaults + "Other"
  const dropdownOptions = buildDropdownOptions(config);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowDropdown(false);
        if (isTypingOther && otherText.trim()) {
          commitOther();
        } else if (isTypingOther) {
          setIsTypingOther(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTypingOther, otherText]);

  useEffect(() => {
    if (isTypingOther && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTypingOther]);

  const commitOther = () => {
    if (otherText.trim()) {
      const val = otherText.trim();
      onChange(config.id, val);
      if (saveToTemplate && onSaveToTemplate) {
        onSaveToTemplate(config.id, val);
      }
      setIsTypingOther(false);
      setOtherText('');
      setSaveToTemplate(false);
    }
  };

  const handleMainClick = () => {
    if (isResolved) {
      // Undo: revert to unresolved state
      onChange(config.id, '');
      return;
    }
    const defaultVal = config.attestationConfirmedLabel
      ?? config.defaultValue
      ?? config.options?.[0]
      ?? config.attestationLabel
      ?? 'Confirmed';
    onChange(config.id, defaultVal);
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleSelect = (option: string) => {
    if (option === '__OTHER__') {
      setShowDropdown(false);
      setIsTypingOther(true);
      setOtherText('');
      return;
    }
    onChange(config.id, option);
    if (saveToTemplate && onSaveToTemplate) {
      onSaveToTemplate(config.id, option);
    }
    setShowDropdown(false);
    setSaveToTemplate(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitOther();
    }
  };

  // "Other" typing mode
  if (isTypingOther) {
    return (
      <span ref={ref} id={`resolution-${config.id}`} className="inline-flex items-center gap-1 align-baseline mx-0.5">
        <input
          ref={inputRef}
          type="text"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (otherText.trim()) commitOther(); }}
          placeholder="Type variance..."
          className="inline-block w-48 h-7 px-2 text-sm border border-primary rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <AssistTooltip config={config} isResolved={false} />
      </span>
    );
  }

  // Resolved display label
  const resolvedLabel = config.attestation
    ? `✓ ${config.attestationConfirmedLabel ?? value}`
    : `✓ ${value}`;

  // Unresolved display label
  const unresolvedLabel = config.attestation
    ? config.attestationLabel
    : `✓ Verify: ${config.defaultValue ?? config.options?.[0] ?? config.label}`;

  return (
    <span
      ref={ref}
      id={`resolution-${config.id}`}
      data-compliance-gap={config.id}
      className={cn(
        "inline-flex items-center gap-1 align-baseline mx-0.5 flex-wrap transition-all duration-500",
        highlighted && !isResolved && "rounded-lg ring-2 ring-amber-400 ring-offset-2 ring-offset-background bg-amber-50/60 px-1 py-0.5"
      )}
    >
      {/* DocuSign-style highlight ring only — no arrow */}
      <span className="relative inline-flex items-center flex-wrap">
        {/* Main button */}
        <button
          onClick={handleMainClick}
          className={cn(
            'inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer border whitespace-normal text-left',
            'rounded-l-md px-2 sm:px-2.5 py-1',
            'print:bg-transparent print:text-black print:border-none print:p-0 print:font-normal print:inline print:shadow-none',
            isResolved
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
              : 'bg-muted text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground'
          )}
        >
          {isResolved ? resolvedLabel : unresolvedLabel}
          <span className={cn(
            'w-1.5 h-1.5 rounded-full shrink-0 print:hidden',
            isResolved ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          )} />
        </button>

        {/* Chevron — always present */}
        <button
          onClick={handleChevronClick}
          className={cn(
            'inline-flex items-center justify-center w-7 py-1 border border-l-0 rounded-r-md transition-colors print:hidden',
            isResolved
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
              : 'bg-muted text-muted-foreground border-border hover:bg-accent'
          )}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 z-50 min-w-[260px] rounded-lg border bg-popover shadow-lg py-1">
            {dropdownOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm transition-colors',
                  opt.value === '__OTHER__'
                    ? 'border-t border-border text-primary font-medium hover:bg-primary/5 mt-0.5 pt-2'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
              >
                {opt.label}
              </button>
            ))}

            {/* Save to Master Template toggle with Surgeon Assist note */}
            {onSaveToTemplate && (
              <>
                <Separator className="my-1" />
                <div className="px-3 py-2 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label
                      htmlFor={`save-template-${config.id}`}
                      className="text-[11px] text-muted-foreground cursor-pointer select-none leading-tight"
                    >
                      Save as default in my Master Template
                    </label>
                    <Switch
                      id={`save-template-${config.id}`}
                      checked={saveToTemplate}
                      onCheckedChange={setSaveToTemplate}
                      className="scale-75 shrink-0"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 leading-snug flex items-start gap-1">
                    <Sparkles className="w-3 h-3 text-amber-500 shrink-0 mt-px" />
                    <span>Enabling this will pre-fill this answer on all future cases using this template — no re-verification needed.</span>
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </span>

      <span className="print:hidden"><AssistTooltip config={config} isResolved={isResolved} /></span>
    </span>
  );
}

/** Build the dropdown options list, always ending with "Other" */
function buildDropdownOptions(config: ResolutionFieldConfig): { label: string; value: string }[] {
  const opts: { label: string; value: string }[] = [];

  // Add explicit options if present
  if (config.options) {
    config.options.forEach(o => opts.push({ label: o, value: o }));
  }

  // For attestation-only fields, add the default as an option if not already present
  if (config.attestation && config.attestationConfirmedLabel) {
    const exists = opts.some(o => o.value === config.attestationConfirmedLabel);
    if (!exists) {
      opts.unshift({ label: config.attestationConfirmedLabel!, value: config.attestationConfirmedLabel! });
    }
  }

  // If there's a defaultValue not already present, add it
  if (config.defaultValue && !opts.some(o => o.value === config.defaultValue)) {
    opts.unshift({ label: config.defaultValue, value: config.defaultValue });
  }

  // Always add "Other" at the end
  opts.push({ label: 'Other (Type variance)...', value: '__OTHER__' });

  return opts;
}

function AssistTooltip({ config, isResolved }: { config: ResolutionFieldConfig; isResolved: boolean }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center w-4 h-4 cursor-help">
            <Sparkles className={cn(
              'w-3.5 h-3.5 transition-colors',
              isResolved ? 'text-emerald-500' : 'text-amber-500'
            )} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          <p className="text-xs font-medium">Surgeon Assist</p>
          <p className="text-xs text-muted-foreground mt-0.5">{config.assistText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
